(function () {
  'use strict';

  // Scroll reveal observer — adds .in-view when a .reveal / .reveal-headline /
  // .reveal-blur element enters the viewport. Elements inside .hero are marked
  // .in-view at render time so the hero paints immediately; the observer clears
  // any stray .in-view on non-hero elements and then re-adds it on scroll.
  //
  // In Shopify Theme Editor (design mode), the IntersectionObserver doesn't
  // re-attach to re-rendered sections, leaving content stuck invisible. So
  // when designMode is detected, we force every reveal element visible
  // immediately (no animation) — merchants can preview their edits.
  var isDesignMode = window.Shopify && window.Shopify.designMode;
  if (isDesignMode) {
    var forceVisible = function () {
      document.querySelectorAll('.reveal, .reveal-headline, .reveal-blur').forEach(function (el) {
        el.classList.add('in-view');
      });
    };
    forceVisible();
    document.addEventListener('shopify:section:load', forceVisible);
    document.addEventListener('shopify:section:select', forceVisible);
    document.addEventListener('shopify:block:select', forceVisible);
  } else if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    document.querySelectorAll('.reveal, .reveal-headline, .reveal-blur').forEach(function (el) {
      if (!el.closest('.hero')) el.classList.remove('in-view');
      observer.observe(el);
    });
  }

  // Count-up stats — eases an integer from 0 to data-count-to over
  // 1.6s when the element scrolls into view. Reduced-motion users
  // see the final value with no animation.
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function formatNum(n) { return Math.round(n).toLocaleString('en-US'); }
  function runCountUp(el) {
    var target = parseInt(el.dataset.countTo, 10);
    var suffix = el.dataset.countSuffix || '';
    if (isNaN(target)) return;
    if (reducedMotion) {
      el.textContent = formatNum(target) + suffix;
      return;
    }
    var duration = 1600;
    var start = performance.now();
    function step(now) {
      var t = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = formatNum(target * eased) + suffix;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  if ('IntersectionObserver' in window) {
    var countObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          runCountUp(entry.target);
          countObs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('[data-count-to]').forEach(function (el) { countObs.observe(el); });
  } else {
    document.querySelectorAll('[data-count-to]').forEach(runCountUp);
  }

  // FAQ accordion — opening one item closes any others in the same group.
  document.querySelectorAll('.about-faq-list').forEach(function (group) {
    group.querySelectorAll('details').forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        group.querySelectorAll('details').forEach(function (other) {
          if (other !== item) other.removeAttribute('open');
        });
      });
    });
  });

  // Product page — variant logic, gallery, qty, sticky ATC. The
  // section emits a JSON blob of all variants (id, options, price,
  // available, formatted price strings) so we can update price + ATC
  // state client-side without a server round-trip on each chip click.
  (function () {
    var root = document.querySelector('[data-pdp-root]');
    if (!root) return;

    var variantsScript = document.querySelector('[data-pdp-variants]');
    var variants = [];
    try { variants = variantsScript ? JSON.parse(variantsScript.textContent) : []; }
    catch (e) { variants = []; }

    var idInput = document.querySelector('[data-pdp-variant-id]');
    var priceDisplay = document.querySelector('[data-pdp-price-display]');
    var atcBtn = document.querySelector('[data-pdp-atc-btn]');
    var stickyPrice = document.querySelector('[data-pdp-sticky-price]');
    var stockTag = document.querySelector('[data-pdp-stock-tag]');
    var stockText = document.querySelector('[data-pdp-stock-text]');

    // Build initial selectedOptions from the active chip in each group.
    var groups = document.querySelectorAll('[data-pdp-option-group]');
    var selectedOptions = [];
    groups.forEach(function (group) {
      var active = group.querySelector('.active');
      selectedOptions.push(active ? active.dataset.optionValue : null);
    });

    function findVariant() {
      return variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selectedOptions[i]; });
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;

      // Sync URL so refresh / share preserves selection.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      } catch (e) {}

      if (priceDisplay) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceDisplay.innerHTML = '<span class="old">' + variant.compare_at_price_formatted + '</span>' + variant.price_formatted;
        } else {
          priceDisplay.innerHTML = variant.price_formatted;
        }
      }
      if (stickyPrice) stickyPrice.innerHTML = variant.price_formatted;

      if (atcBtn) {
        if (variant.available) {
          atcBtn.disabled = false;
          atcBtn.innerHTML = 'Add to bag — <span data-pdp-atc-price>' + variant.price_formatted + '</span>';
        } else {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold out';
        }
      }

      if (stockTag) {
        // Mirror the three-state logic from the Liquid template so the
        // tag and ATC button stay in sync on variant change. Variants
        // with inventory_policy: continue have available=true even at
        // 0 inventory, so the ATC stays clickable while the tag shows
        // the preorder copy.
        var hasStock = !variant.inventory_management || variant.inventory_quantity > 0;
        var isInStock = variant.available && hasStock;
        stockTag.classList.toggle('in-stock', isInStock);
        stockTag.classList.toggle('sold-out', !isInStock);
        if (stockText) {
          if (isInStock) {
            stockText.textContent = 'In stock · Ships within 24 hours';
          } else if (variant.available) {
            stockText.textContent = 'Out of stock · Preorder in 7–10 days';
          } else {
            stockText.textContent = 'Out of stock';
          }
        }
      }
    }

    document.querySelectorAll('[data-pdp-option-value]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var pos = parseInt(chip.dataset.optionPosition, 10) - 1;
        var val = chip.dataset.optionValue;
        var groupSelector = '[data-option-position="' + (pos + 1) + '"]';
        document.querySelectorAll(groupSelector + '[data-pdp-option-value]').forEach(function (c) {
          c.classList.remove('active');
        });
        chip.classList.add('active');
        selectedOptions[pos] = val;
        updateForVariant(findVariant());
      });
    });

    // Desktop thumbnail gallery — swap hero src on click. Also flips
    // the hero cell's letterbox mode based on the thumb's data-aspect:
    // square images (size guides) get object-fit: contain; product
    // photos stay at cover. The first thumb's data-aspect is forced
    // to 0.8 server-side so the hero shot is always cover-cropped.
    var hero = document.querySelector('[data-pdp-hero]');
    var heroCell = document.querySelector('[data-pdp-main-img]');
    var thumbs = document.querySelectorAll('[data-pdp-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
        if (hero) hero.src = thumb.dataset.src;
        if (heroCell) {
          var aspect = parseFloat(thumb.dataset.aspect) || 0.8;
          heroCell.classList.toggle('is-square-content', aspect >= 0.95 && aspect <= 1.05);
        }
      });
    });

    // Mobile carousel — track which slide is in view to update dots,
    // and let dot clicks scroll to the matching slide.
    var carousel = document.querySelector('[data-pdp-carousel]');
    var dots = document.querySelectorAll('[data-pdp-dot]');
    if (carousel && 'IntersectionObserver' in window) {
      var slides = carousel.querySelectorAll('.pdp-carousel-slide');
      var dotObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var idx = Array.prototype.indexOf.call(slides, entry.target);
            dots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });
          }
        });
      }, { root: carousel, threshold: 0.6 });
      slides.forEach(function (s) { dotObs.observe(s); });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
      });
    }

    // Quantity stepper — clamps 1..10 and writes to the hidden input
    // so the form submit carries the correct quantity.
    var qtyVal = document.querySelector('[data-pdp-qty-value]');
    var qtyInput = document.querySelector('[data-pdp-qty-input]');
    var qtyMinus = document.querySelector('[data-pdp-qty-minus]');
    var qtyPlus = document.querySelector('[data-pdp-qty-plus]');
    if (qtyVal && qtyInput && qtyMinus && qtyPlus) {
      var qty = 1;
      function setQty(n) {
        qty = Math.max(1, Math.min(10, n));
        qtyVal.textContent = qty;
        qtyInput.value = qty;
      }
      qtyMinus.addEventListener('click', function () { setQty(qty - 1); });
      qtyPlus.addEventListener('click', function () { setQty(qty + 1); });
    }

    // Sticky ATC — visible only when the main ATC button has scrolled
    // out of view. The sticky button just submits the same form.
    var sticky = document.querySelector('[data-pdp-sticky-atc]');
    if (sticky && atcBtn && 'IntersectionObserver' in window) {
      var stickyObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sticky.classList.toggle('visible', !entry.isIntersecting);
        });
      }, { threshold: 0 });
      stickyObs.observe(atcBtn);
    }
    var stickyBtn = document.querySelector('[data-pdp-sticky-atc-btn]');
    var pdpForm = document.getElementById('pdp-form');
    if (stickyBtn && pdpForm) {
      stickyBtn.addEventListener('click', function () {
        if (atcBtn && atcBtn.disabled) return;
        pdpForm.requestSubmit ? pdpForm.requestSubmit() : pdpForm.submit();
      });
    }

    // Recently viewed — push current product into localStorage and
    // render up to 4 prior PDP visits in the section's grid. The
    // section's wrap is hidden until JS confirms there's at least
    // one prior product to show.
    var current = window.zdCurrentProduct;
    var rvWrap = document.querySelector('[data-recently-viewed-wrap]');
    var rvGrid = document.querySelector('[data-recently-viewed]');
    var STORAGE_KEY = 'zd_recently_viewed';
    var MAX_STORE = 8;
    var SHOW = 4;

    if (current && current.handle) {
      var stored = [];
      try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (e) {}
      stored = stored.filter(function (p) { return p && p.handle && p.handle !== current.handle; });
      stored.unshift(current);
      stored = stored.slice(0, MAX_STORE);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch (e) {}

      // The wrap is rendered server-side with fallback products from
      // the configured collection. If the visitor has prior history,
      // clear those and render their actual recently-viewed list.
      var others = stored.filter(function (p) { return p.handle !== current.handle; });
      if (rvWrap && rvGrid && others.length > 0) {
        rvWrap.removeAttribute('hidden');
        // Clear server-rendered fallback cards.
        rvGrid.innerHTML = '';
        others.slice(0, SHOW).forEach(function (p, i) {
          var card = document.createElement('a');
          card.href = p.url || ('/products/' + p.handle);
          card.className = 'product reveal in-view';
          if (i > 0) card.style.setProperty('--stagger', (i * 60) + 'ms');
          var media = '<div class="product-media">';
          if (p.image) media += '<img class="main-img" src="' + p.image + '" alt="" loading="lazy" width="800" height="1000">';
          media += '</div>';
          card.innerHTML = media + '<div class="product-name">' + (p.title || '') + '</div><div class="product-price">' + (p.price_formatted || '') + '</div>';
          rvGrid.appendChild(card);
        });
      }
    }
  })();

  // Nav "scrolled" class — subtle backdrop shift once the user scrolls past
  // the hero fold.
  var nav = document.getElementById('nav');
  if (nav) {
    window.addEventListener('scroll', function () {
      if (window.scrollY > 40) nav.classList.add('scrolled');
      else nav.classList.remove('scrolled');
    }, { passive: true });
  }

  // Hero parallax — translateY + slight scale on the hero image as the user
  // scrolls. Desktop only (disabled below the 750px nav-collapse breakpoint).
  var heroImg = document.getElementById('hero-img');
  if (heroImg && window.innerWidth > 750) {
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var y = Math.min(window.scrollY, 600);
          heroImg.style.transform = 'translateY(' + (y * 0.22) + 'px) scale(' + (1 + y * 0.00015) + ')';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // Price filter — chip popover toggle, URL-state hydration, and
  // form-submit cleanup. Shopify Liquid can't read arbitrary query
  // params, so we read filter.v.price.gte / filter.v.price.lte from
  // window.location here to render the chip's active label and
  // pre-populate the popover inputs.
  document.querySelectorAll('[data-price-filter-wrap]').forEach(function (wrap) {
    var toggle = wrap.querySelector('[data-price-filter-toggle]');
    var popover = wrap.querySelector('[data-price-filter-popover]');
    var backdrop = wrap.querySelector('[data-price-filter-backdrop]');
    var closeBtn = wrap.querySelector('[data-price-filter-close]');
    var label = wrap.querySelector('[data-price-filter-label]');
    var clearBtn = wrap.querySelector('[data-price-filter-clear]');
    var resetLink = wrap.querySelector('[data-price-filter-reset]');
    var minInput = wrap.querySelector('[data-price-filter-min]');
    var maxInput = wrap.querySelector('[data-price-filter-max]');
    if (!toggle || !popover) return;

    // Hydrate from URL.
    var params = new URLSearchParams(window.location.search);
    var rawMin = parseInt(params.get('filter.v.price.gte'), 10);
    var rawMax = parseInt(params.get('filter.v.price.lte'), 10);
    var hasMin = !isNaN(rawMin) && rawMin > 0;
    var hasMax = !isNaN(rawMax) && rawMax > 0;
    if (hasMin && minInput) minInput.value = rawMin;
    if (hasMax && maxInput) maxInput.value = rawMax;

    if (hasMin || hasMax) {
      toggle.classList.add('is-applied');
      if (label) {
        if (hasMin && hasMax) label.textContent = 'RM ' + rawMin + '–' + rawMax;
        else if (hasMin) label.textContent = 'RM ' + rawMin + '+';
        else label.textContent = 'Up to RM ' + rawMax;
      }
      if (clearBtn) {
        clearBtn.hidden = false;
        // Build clear URL preserving every param except the price ones.
        var clearParams = new URLSearchParams(window.location.search);
        clearParams.delete('filter.v.price.gte');
        clearParams.delete('filter.v.price.lte');
        var clearQs = clearParams.toString();
        clearBtn.href = window.location.pathname + (clearQs ? '?' + clearQs : '');
      }
    } else if (resetLink) {
      resetLink.setAttribute('data-disabled', '');
    }

    // Submit cleanup — drop empty inputs, swap if min > max.
    var form = popover;
    form.addEventListener('submit', function () {
      var minVal = minInput && minInput.value ? parseInt(minInput.value, 10) : 0;
      var maxVal = maxInput && maxInput.value ? parseInt(maxInput.value, 10) : 0;
      if (minVal > 0 && maxVal > 0 && minVal > maxVal) {
        var tmp = minVal; minVal = maxVal; maxVal = tmp;
        if (minInput) minInput.value = minVal;
        if (maxInput) maxInput.value = maxVal;
      }
      if (minInput && (!minVal || minVal <= 0)) minInput.disabled = true;
      if (maxInput && (!maxVal || maxVal <= 0)) maxInput.disabled = true;
    });

    // Portal the popover to <body> on open so it escapes the filter
    // bar's backdrop-filter stacking context (which otherwise causes
    // the popover to render visually clipped/muddied on desktop).
    // We position it via window-coords from the toggle's bounding rect.
    function positionPortaled() {
      if (!popover.classList.contains('is-portaled')) return;
      var rect = toggle.getBoundingClientRect();
      var topPx = rect.bottom + 10;
      var rightPx = Math.max(16, window.innerWidth - rect.right);
      popover.style.top = topPx + 'px';
      popover.style.right = rightPx + 'px';
    }

    function close() {
      popover.classList.remove('is-visible');
      if (backdrop) backdrop.classList.remove('is-visible');
      setTimeout(function () {
        popover.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-open');
        // De-portal: return the popover to its original wrap so the
        // DOM is back to its normal shape between opens.
        if (popover.classList.contains('is-portaled')) {
          popover.classList.remove('is-portaled');
          popover.style.top = '';
          popover.style.right = '';
          wrap.appendChild(popover);
        }
      }, 240);
      toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('scroll', positionPortaled);
      window.removeEventListener('resize', positionPortaled);
    }
    function open() {
      // Move into <body> and switch to viewport-anchored positioning
      // BEFORE adding .is-open so the first paint is correctly placed.
      document.body.appendChild(popover);
      popover.classList.add('is-portaled');
      positionPortaled();

      popover.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      requestAnimationFrame(function () {
        popover.classList.add('is-visible');
        if (backdrop) backdrop.classList.add('is-visible');
      });
      toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', onKey);
      document.addEventListener('click', onDocClick);
      window.addEventListener('scroll', positionPortaled, { passive: true });
      window.addEventListener('resize', positionPortaled);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    function onDocClick(e) {
      // Popover may be portaled out of wrap, so check it directly too.
      if (wrap.contains(e.target) || popover.contains(e.target)) return;
      if (backdrop && backdrop.contains(e.target)) { close(); return; }
      close();
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (popover.classList.contains('is-open')) close(); else open();
    });
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) { e.preventDefault(); close(); });
    }
    if (backdrop) {
      backdrop.addEventListener('click', function () { close(); });
    }
  });

  // PDP Mix & Match — APM-style inline bundle card. Mounts visible
  // with a skeleton, fetches complementary recommendations (falling
  // back to related), enriches each with full variant data, renders
  // rows with toggle + variant selector + live total, and posts
  // current product variant + selected pairings in a single
  // cart/add.js call on bundle ATC. Animations: card fade+slide
  // on mount, row collapse on toggle-off, total pulse on recalc,
  // CTA check-mark on success.
  var mixCard = document.querySelector('[data-pdp-mix-match]');
  if (mixCard) {
    var mixSkeleton = mixCard.querySelector('[data-pdp-mix-match-skeleton]');
    var mixRowsEl = mixCard.querySelector('[data-pdp-mix-match-rows]');
    var mixTotalRow = mixCard.querySelector('[data-pdp-mix-match-total-row]');
    var mixTotalEl = mixCard.querySelector('[data-pdp-mix-match-total]');
    var mixCtaBtn = mixCard.querySelector('[data-pdp-mix-match-cta]');
    var mixCtaText = mixCard.querySelector('[data-pdp-mix-match-cta-text]');
    var mixRowTemplate = document.querySelector('[data-pdp-mix-match-row-template]');
    var mixProductId = mixCard.dataset.productId;
    var mixLimit = parseInt(mixCard.dataset.limit, 10) || 2;
    var mixPairings = []; // { product, selectedVariantId, included, rowEl }
    var mixPulseTimer = null;

    // Reveal the card with its skeleton immediately so the slot is
    // accounted for while we fetch — avoids a layout jump when data
    // lands. Hide if the API ultimately returns nothing.
    mixCard.hidden = false;
    requestAnimationFrame(function () {
      mixCard.classList.add('is-mounted');
    });

    function mixFormatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      var parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return 'RM ' + parts.join('.');
    }

    function mixCurrentVariant() {
      var idEl = document.querySelector('[data-pdp-variant-id]');
      var variantsEl = document.querySelector('[data-pdp-variants]');
      if (!idEl || !variantsEl) return null;
      var id = parseInt(idEl.value, 10);
      try {
        var variants = JSON.parse(variantsEl.textContent);
        return variants.find(function (v) { return v.id === id; }) || null;
      } catch (e) { return null; }
    }

    function mixCurrentQty() {
      var qtyInput = document.querySelector('[data-pdp-qty-input]');
      return qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    }

    function mixRecalcTotal() {
      var total = 0;
      var current = mixCurrentVariant();
      if (current) total += current.price * mixCurrentQty();
      mixPairings.forEach(function (p) {
        if (!p.included) return;
        var variant = p.product.variants.find(function (v) { return v.id === p.selectedVariantId; });
        if (variant) total += variant.price;
      });
      var formatted = mixFormatMoney(total);
      if (mixTotalEl.textContent !== formatted) {
        mixTotalEl.textContent = formatted;
        mixTotalEl.classList.add('is-pulsing');
        if (mixPulseTimer) clearTimeout(mixPulseTimer);
        mixPulseTimer = setTimeout(function () {
          mixTotalEl.classList.remove('is-pulsing');
        }, 280);
      }
    }

    // Options excluded from cohesion — size scales differ across
    // product types (ring sizes, necklace lengths, bracelet sizes
    // are not interchangeable), so matching by Material/Color/Finish
    // only is the safer default.
    var MIX_COHESION_BLOCKLIST = ['size', 'length'];

    function mixGetParentOptionMap() {
      var optionNames = (window.zdCurrentProduct && window.zdCurrentProduct.option_names) || [];
      var current = mixCurrentVariant();
      if (!current || !current.options) return {};
      var map = {};
      optionNames.forEach(function (name, i) {
        if (name) map[name] = current.options[i];
      });
      return map;
    }

    function mixFindCohesiveVariant(parentOpts, pairingProduct) {
      if (!pairingProduct.variants || !pairingProduct.variants.length) return null;
      var optionNames = pairingProduct.options || [];
      var preferred = optionNames.map(function (name) {
        if (!name) return null;
        if (MIX_COHESION_BLOCKLIST.indexOf(String(name).toLowerCase()) !== -1) return null;
        return parentOpts[name] || null;
      });
      var hasPreference = preferred.some(Boolean);
      var firstAvailable = pairingProduct.variants.find(function (v) { return v.available; })
        || pairingProduct.variants[0];
      if (!hasPreference) return firstAvailable;

      var best = null;
      var bestScore = -1;
      pairingProduct.variants.forEach(function (v) {
        var score = 0;
        optionNames.forEach(function (name, i) {
          if (preferred[i] && v.options[i] === preferred[i]) score++;
        });
        // Prefer available variants when scores tie.
        if (score > bestScore || (score === bestScore && best && !best.available && v.available)) {
          bestScore = score;
          best = v;
        }
      });
      return best || firstAvailable;
    }

    function mixPopulateRow(rowEl, product, preferredVariant) {
      var thumb = rowEl.querySelector('[data-pdp-mix-match-thumb]');
      var titleLink = rowEl.querySelector('[data-pdp-mix-match-title-link]');
      var priceEl = rowEl.querySelector('[data-pdp-mix-match-price]');
      var variantSelect = rowEl.querySelector('[data-pdp-mix-match-variant]');

      if (product.featured_image) {
        thumb.src = product.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '_400x500$1');
        thumb.alt = product.title;
      }
      titleLink.textContent = product.title;
      titleLink.href = product.url;

      var defaultVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
      var initial = preferredVariant || defaultVariant;
      if (initial) priceEl.textContent = mixFormatMoney(initial.price);

      if (product.variants.length > 1) {
        variantSelect.hidden = false;
        product.variants.forEach(function (v) {
          var opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = v.public_title || v.title;
          if (!v.available) {
            opt.disabled = true;
            opt.textContent += ' — Sold out';
          }
          if (initial && v.id === initial.id) opt.selected = true;
          variantSelect.appendChild(opt);
        });
      }
      return initial;
    }

    function mixRecohesion() {
      var parentOpts = mixGetParentOptionMap();
      mixPairings.forEach(function (p) {
        var newVariant = mixFindCohesiveVariant(parentOpts, p.product);
        if (!newVariant || newVariant.id === p.selectedVariantId) return;
        p.selectedVariantId = newVariant.id;
        var priceEl = p.rowEl.querySelector('[data-pdp-mix-match-price]');
        if (priceEl) priceEl.textContent = mixFormatMoney(newVariant.price);
        var variantSelect = p.rowEl.querySelector('[data-pdp-mix-match-variant]');
        if (variantSelect && !variantSelect.hidden) variantSelect.value = String(newVariant.id);
        p.rowEl.classList.remove('is-cohered');
        // Force reflow to restart animation if it's already in progress.
        void p.rowEl.offsetWidth;
        p.rowEl.classList.add('is-cohered');
        setTimeout(function () { p.rowEl.classList.remove('is-cohered'); }, 600);
      });
    }

    function mixFetchProduct(handle) {
      return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    function mixFetchRecommendations(intent, limit) {
      var url = '/recommendations/products.json?product_id=' + mixProductId
        + '&intent=' + intent + '&limit=' + (limit || mixLimit);
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    // Try complementary first (merchant-curated via Search & Discovery,
    // or co-purchase signal). Fall back to related when complementary
    // returns nothing. We over-fetch (mixLimit * 4, capped at 10 — the
    // API's max) so the swap-cycle button has a deeper pool to draw
    // alternates from. Each visible slot gets its own slice of the
    // pool, indexed round-robin so two slots never show the same item.
    function mixPoolSize() {
      return Math.min(10, Math.max(mixLimit * 4, mixLimit));
    }
    function mixGetRecommendations() {
      var poolSize = mixPoolSize();
      return mixFetchRecommendations('complementary', poolSize).then(function (data) {
        if (data && data.products && data.products.length) return data;
        return mixFetchRecommendations('related', poolSize);
      });
    }

    function mixHideCard() {
      mixCard.classList.remove('is-mounted');
      setTimeout(function () { mixCard.hidden = true; }, 320);
    }

    function mixSwapInRows() {
      if (mixSkeleton) mixSkeleton.hidden = true;
      mixRowsEl.hidden = false;
      mixTotalRow.hidden = false;
      mixCtaBtn.hidden = false;
    }

    function mixWireRow(pairing, pool) {
      var rowEl = pairing.rowEl;

      var toggle = rowEl.querySelector('[data-pdp-mix-match-toggle]');
      toggle.addEventListener('change', function () {
        pairing.included = toggle.checked;
        rowEl.classList.toggle('is-excluded', !toggle.checked);
        mixRecalcTotal();
      });

      var variantSelect = rowEl.querySelector('[data-pdp-mix-match-variant]');
      variantSelect.addEventListener('change', function () {
        pairing.selectedVariantId = parseInt(variantSelect.value, 10);
        var v = pairing.product.variants.find(function (x) { return x.id === pairing.selectedVariantId; });
        if (v) rowEl.querySelector('[data-pdp-mix-match-price]').textContent = mixFormatMoney(v.price);
        mixRecalcTotal();
      });

      var swapBtn = rowEl.querySelector('[data-pdp-mix-match-swap]');
      var slotCount = mixPairings.length; // captured at wire time; pool is shared so this is fine after first wire-pass below
      // Show swap only when the pool has more candidates than visible slots.
      if (pool.length > Math.max(slotCount, 1)) swapBtn.hidden = false;

      swapBtn.addEventListener('click', function () {
        if (swapBtn.disabled) return;
        var step = mixPairings.length || 1;
        var nextIndex = pairing.poolIndex;
        // Walk forward by `step` until we find a product not currently
        // shown in another slot. Bounded by pool length to avoid loops.
        for (var i = 0; i < pool.length; i++) {
          nextIndex = (nextIndex + step) % pool.length;
          var taken = mixPairings.some(function (p) {
            return p !== pairing && p.poolIndex === nextIndex;
          });
          if (!taken && nextIndex !== pairing.poolIndex) break;
        }
        if (nextIndex === pairing.poolIndex) return;

        var nextProduct = pool[nextIndex];
        if (!nextProduct) return;

        swapBtn.disabled = true;
        rowEl.classList.add('is-swapping');
        setTimeout(function () {
          // Reset variant select before repopulating.
          variantSelect.innerHTML = '';
          variantSelect.hidden = true;
          var parentOpts = mixGetParentOptionMap();
          var preferred = mixFindCohesiveVariant(parentOpts, nextProduct);
          var initial = mixPopulateRow(rowEl, nextProduct, preferred);
          pairing.product = nextProduct;
          pairing.selectedVariantId = initial ? initial.id : nextProduct.variants[0].id;
          pairing.poolIndex = nextIndex;
          rowEl.classList.remove('is-swapping');
          rowEl.classList.add('is-swapped-in');
          setTimeout(function () { rowEl.classList.remove('is-swapped-in'); }, 320);
          swapBtn.disabled = false;
          mixRecalcTotal();
        }, 200);
      });
    }

    mixGetRecommendations().then(function (data) {
      if (!data || !data.products || !data.products.length) {
        mixHideCard();
        return;
      }
      var handles = data.products.map(function (p) { return p.handle; });
      return Promise.all(handles.map(mixFetchProduct)).then(function (results) {
        var pool = results.filter(Boolean);
        if (!pool.length) {
          mixHideCard();
          return;
        }
        var slotCount = Math.min(mixLimit, pool.length);
        // First pass: build pairings + rows with initial poolIndex = i.
        // Initial variant chosen via cohesion against the parent's
        // current selection so the bundle reads as a styled set
        // (matching finish/material) on first render.
        var initialParentOpts = mixGetParentOptionMap();
        for (var i = 0; i < slotCount; i++) {
          var product = pool[i];
          var rowEl = mixRowTemplate.content.firstElementChild.cloneNode(true);
          var preferred = mixFindCohesiveVariant(initialParentOpts, product);
          var initial = mixPopulateRow(rowEl, product, preferred);
          if (!initial) continue;
          var pairing = {
            product: product,
            selectedVariantId: initial.id,
            included: true,
            poolIndex: i,
            rowEl: rowEl
          };
          mixPairings.push(pairing);
          mixRowsEl.appendChild(rowEl);
        }
        // Second pass: wire events now that slotCount is known.
        mixPairings.forEach(function (p) { mixWireRow(p, pool); });
        mixSwapInRows();
        mixRecalcTotal();
      });
    });

    // Re-cohesion + recalc total when the parent product's variant
    // changes via chip click. setTimeout(0) defers until the existing
    // PDP variant logic has updated [data-pdp-variant-id].
    document.querySelectorAll('[data-pdp-option-value]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        setTimeout(function () { mixRecohesion(); mixRecalcTotal(); }, 0);
      });
    });
    var mixQtyMinus = document.querySelector('[data-pdp-qty-minus]');
    var mixQtyPlus = document.querySelector('[data-pdp-qty-plus]');
    if (mixQtyMinus) mixQtyMinus.addEventListener('click', function () { setTimeout(mixRecalcTotal, 0); });
    if (mixQtyPlus) mixQtyPlus.addEventListener('click', function () { setTimeout(mixRecalcTotal, 0); });

    mixCtaBtn.addEventListener('click', function () {
      var current = mixCurrentVariant();
      if (!current) return;
      var items = [{ id: current.id, quantity: mixCurrentQty() }];
      mixPairings.forEach(function (p) {
        if (p.included) items.push({ id: p.selectedVariantId, quantity: 1 });
      });

      mixCtaBtn.disabled = true;
      var origText = mixCtaText.textContent;
      mixCtaText.textContent = 'Adding…';

      var addPromise = (window.ZD && window.ZD.cartAdd)
        ? window.ZD.cartAdd({ items: items }, { source: 'mix-match' })
        : fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          });

      addPromise
        .then(function () {
          mixCtaText.textContent = 'Added';
          mixCtaBtn.classList.add('is-success');
          setTimeout(function () {
            mixCtaText.textContent = origText;
            mixCtaBtn.classList.remove('is-success');
            mixCtaBtn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          mixCtaText.textContent = 'Try again';
          setTimeout(function () {
            mixCtaText.textContent = origText;
            mixCtaBtn.disabled = false;
          }, 2000);
        });
    });
  }
})();

// Drawer nav — drill-down sub-panel navigation. Buttons with
// [data-drawer-open-sub="<name>"] slide the matching panel in;
// [data-drawer-back] returns to the primary panel. Drawer close
// (handled by drawers.js) triggers a reset so the next open lands
// on the primary panel.
(function () {
  'use strict';
  var drawer = document.querySelector('[data-drawer-nav]');
  if (!drawer) return;
  var primary = drawer.querySelector('.drawer-nav-panel[data-panel="root"]');
  if (!primary) return;
  var subs = drawer.querySelectorAll('.drawer-nav-panel[data-panel]:not([data-panel="root"])');

  function showPrimary() {
    primary.classList.remove('is-stashed');
    primary.classList.add('is-active');
    primary.setAttribute('aria-hidden', 'false');
    subs.forEach(function (p) {
      p.classList.remove('is-active');
      p.classList.add('is-incoming');
      p.setAttribute('aria-hidden', 'true');
    });
    drawer.querySelectorAll('[data-drawer-open-sub]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  function openSub(name) {
    primary.classList.remove('is-active');
    primary.classList.add('is-stashed');
    primary.setAttribute('aria-hidden', 'true');
    subs.forEach(function (p) {
      var match = p.getAttribute('data-panel') === name;
      p.classList.toggle('is-active', match);
      p.classList.toggle('is-incoming', !match);
      p.setAttribute('aria-hidden', match ? 'false' : 'true');
    });
    var trigger = drawer.querySelector('[data-drawer-open-sub="' + name + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // Move focus into the freshly-opened sub-panel so keyboard/screen
    // reader users land on the sub-panel's first interactive element.
    var active = drawer.querySelector('.drawer-nav-panel.is-active');
    if (active) {
      var firstFocusable = active.querySelector('button, a');
      if (firstFocusable) {
        setTimeout(function () { firstFocusable.focus(); }, 60);
      }
    }
  }

  drawer.addEventListener('click', function (e) {
    var sub = e.target.closest('[data-drawer-open-sub]');
    if (sub) {
      e.preventDefault();
      openSub(sub.getAttribute('data-drawer-open-sub'));
      return;
    }
    var back = e.target.closest('[data-drawer-back]');
    if (back) {
      e.preventDefault();
      showPrimary();
    }
  });

  // Reset to primary whenever the drawer is closed so reopening always
  // lands on the top-level menu. drawers.js sets aria-hidden="true" on
  // close — observe that attribute.
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.attributeName === 'aria-hidden' && drawer.getAttribute('aria-hidden') === 'true') {
        // Tiny delay so the slide-out animation finishes before resetting.
        setTimeout(showPrimary, 320);
      }
    });
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
})();

// Localization form — auto-submit when the buyer changes country/currency.
(function () {
  'use strict';
  document.querySelectorAll('[data-localization-select]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      if (sel.form) sel.form.submit();
    });
  });
})();

// Custom inquiry form chips — clicking a chip toggles its active
// state and writes the value into the matching hidden input so the
// chip selection rides with the contact form submission.
(function () {
  'use strict';
  document.querySelectorAll('[data-chip-group]').forEach(function (group) {
    var name = group.getAttribute('data-chip-group');
    var input = document.querySelector('[data-chip-input="' + name + '"]');
    group.querySelectorAll('.custom-form-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var value = chip.getAttribute('data-value');
        var alreadyActive = chip.classList.contains('is-active');
        group.querySelectorAll('.custom-form-chip').forEach(function (c) {
          c.classList.remove('is-active');
        });
        if (!alreadyActive) {
          chip.classList.add('is-active');
          if (input) input.value = value;
        } else {
          if (input) input.value = '';
        }
      });
    });
  });
})();

// Hall of Fame filter — chip click hides pieces whose category
// doesn't match. Pure DOM toggle, no AJAX.
(function () {
  'use strict';
  var band = document.querySelector('[data-hof-filters]');
  if (!band) return;
  var chips = band.querySelectorAll('[data-hof-filter]');
  var pieces = document.querySelectorAll('.hof-piece');
  chips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      var filter = chip.getAttribute('data-hof-filter');
      chips.forEach(function (c) { c.classList.remove('is-active'); });
      chip.classList.add('is-active');
      pieces.forEach(function (p) {
        if (filter === 'all' || p.getAttribute('data-hof-category') === filter) {
          p.style.display = '';
        } else {
          p.style.display = 'none';
        }
      });
    });
  });
})();

// Search overlay — slides down from top, fetches Shopify predictive
// search results as the buyer types. Submitting the form (Enter)
// falls back to /search?q=… so users always have a path forward.
(function () {
  'use strict';
  var overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;
  var input = overlay.querySelector('[data-search-input]');
  var resultsEl = overlay.querySelector('[data-search-results]');
  var form = overlay.querySelector('form');
  if (!input || !resultsEl || !form) return;

  var openTriggers = document.querySelectorAll('[data-search-open]');
  var closeTriggers = overlay.querySelectorAll('[data-search-close]');
  var debounceTimer = null;
  var lastQuery = '';
  var savedScrollY = 0;
  var isOpen = false;
  var lastTrigger = null;

  function lockScroll() {
    savedScrollY = window.scrollY || 0;
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.classList.add('drawer-open');
  }
  function unlockScroll() {
    document.body.classList.remove('drawer-open');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  function open(trigger) {
    if (isOpen) return;
    isOpen = true;
    lastTrigger = trigger || null;
    overlay.setAttribute('aria-hidden', 'false');
    if (lastTrigger && lastTrigger.setAttribute) lastTrigger.setAttribute('aria-expanded', 'true');
    lockScroll();
    setTimeout(function () { input.focus(); }, 60);
  }
  function close() {
    if (!isOpen) return;
    isOpen = false;
    overlay.setAttribute('aria-hidden', 'true');
    if (lastTrigger && lastTrigger.setAttribute) lastTrigger.setAttribute('aria-expanded', 'false');
    unlockScroll();
    if (lastTrigger) {
      try { lastTrigger.focus(); } catch (e) { /* noop */ }
    }
  }

  openTriggers.forEach(function (t) {
    t.addEventListener('click', function (e) {
      e.preventDefault();
      open(t);
    });
  });
  closeTriggers.forEach(function (t) {
    t.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  });
  document.addEventListener('keydown', function (e) {
    if (isOpen && e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  });

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderEmpty(message) {
    resultsEl.innerHTML = '<div class="search-empty mono">' + escapeHtml(message) + '</div>';
  }

  function renderResults(data, query) {
    var resources = data && data.resources && data.resources.results || {};
    var products = resources.products || [];
    var articles = resources.articles || [];
    var pages = resources.pages || [];
    var collections = resources.collections || [];
    var html = '';

    if (products.length) {
      html += '<div class="search-results-group">';
      html += '<div class="search-results-heading">Products</div>';
      html += '<div class="search-products">';
      products.forEach(function (p) {
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        html += '<a class="search-product" href="' + escapeHtml(p.url) + '">';
        html += '<div class="search-product-media">';
        if (img) html += '<img src="' + escapeHtml(img) + '" alt="" loading="lazy">';
        html += '</div>';
        html += '<div class="search-product-name">' + escapeHtml(p.title) + '</div>';
        if (p.price) html += '<div class="search-product-price">' + escapeHtml(p.price) + '</div>';
        html += '</a>';
      });
      html += '</div></div>';
    }

    if (collections.length) {
      html += '<div class="search-results-group">';
      html += '<div class="search-results-heading">Collections</div>';
      html += '<div class="search-link-list">';
      collections.forEach(function (c) {
        html += '<a class="search-link" href="' + escapeHtml(c.url) + '">' + escapeHtml(c.title) + '</a>';
      });
      html += '</div></div>';
    }

    if (pages.length) {
      html += '<div class="search-results-group">';
      html += '<div class="search-results-heading">Pages</div>';
      html += '<div class="search-link-list">';
      pages.forEach(function (p) {
        html += '<a class="search-link" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>';
      });
      html += '</div></div>';
    }

    if (articles.length) {
      html += '<div class="search-results-group">';
      html += '<div class="search-results-heading">Journal</div>';
      html += '<div class="search-link-list">';
      articles.forEach(function (a) {
        html += '<a class="search-link" href="' + escapeHtml(a.url) + '">' + escapeHtml(a.title) + '</a>';
      });
      html += '</div></div>';
    }

    if (!html) {
      renderEmpty('No matches for "' + query + '"');
      return;
    }
    html += '<a class="search-view-all" href="/search?q=' + encodeURIComponent(query) + '">View all results &rarr;</a>';
    resultsEl.innerHTML = html;
  }

  function fetchSuggestions(query) {
    if (!query) {
      renderEmpty('— Start typing to search');
      return;
    }
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection,page,article' +
      '&resources[limit]=6&resources[options][unavailable_products]=last';
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) { if (query === lastQuery) renderResults(data, query); })
      .catch(function () { renderEmpty('Search is taking a moment — press Enter to try the full search page.'); });
  }

  input.addEventListener('input', function () {
    var q = input.value.trim();
    lastQuery = q;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { fetchSuggestions(q); }, 220);
  });

  // Form submission: let it go to /search?q= naturally for the full results page.
  form.addEventListener('submit', function (e) {
    if (!input.value.trim()) {
      // Block empty submits — just stay on overlay.
      e.preventDefault();
    }
  });
})();

// Lightbox modal — used by Custom-page process-step thumbnails. Click
// any [data-lightbox-src] trigger to open the full-size image; Escape
// or outside-click or X closes.
(function () {
  'use strict';
  var modal = document.querySelector('[data-lightbox-modal]');
  if (!modal) return;
  var img = modal.querySelector('[data-lightbox-img]');
  var closeBtn = modal.querySelector('[data-lightbox-close]');

  function open(src, alt) {
    if (img) {
      img.src = src;
      img.alt = alt || '';
    }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
  }
  function close() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    if (img) { img.removeAttribute('src'); img.alt = ''; }
  }
  function onKey(e) { if (e.key === 'Escape') close(); }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-lightbox-src]');
    if (trigger) {
      e.preventDefault();
      open(trigger.getAttribute('data-lightbox-src'), trigger.getAttribute('data-lightbox-alt'));
      return;
    }
    if (e.target === modal || e.target === closeBtn || (closeBtn && closeBtn.contains(e.target))) {
      if (modal.classList.contains('is-open')) close();
    }
  });
})();

// AJAX cart drawer — POSTs /cart/add.js with section rendering, swaps
// drawer innerHTML, updates header bag count, opens the drawer.
// Used by PDP form submit (intercepted), sticky-ATC (via form.requestSubmit),
// and Mix & Match bundle ATC (via window.ZD.cartAdd).
(function () {
  'use strict';

  function setBagCount(n) {
    var el = document.querySelector('[data-cart-count]');
    if (el) el.textContent = String(n);
  }

  function refreshDrawer(html) {
    if (!html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var fresh = tmp.querySelector('#drawer-cart');
    var live = document.getElementById('drawer-cart');
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  }

  function openCartDrawer() {
    var trigger = document.querySelector('[data-drawer-open="cart"]');
    if (trigger) trigger.click();
  }

  window.ZD = window.ZD || {};

  // body: FormData (from a <form>) OR a plain object like { id, quantity } / { items: [...] }
  // detail: arbitrary data attached to the cart:added CustomEvent
  window.ZD.cartAdd = function (body, detail) {
    var fetchOpts = {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    };

    if (body instanceof FormData) {
      body.append('sections', 'drawer-cart');
      fetchOpts.body = body;
    } else {
      fetchOpts.headers['Content-Type'] = 'application/json';
      var payload = {};
      for (var k in body) if (Object.prototype.hasOwnProperty.call(body, k)) payload[k] = body[k];
      payload.sections = 'drawer-cart';
      fetchOpts.body = JSON.stringify(payload);
    }

    return fetch('/cart/add.js', fetchOpts)
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (data) {
        if (data.sections && data.sections['drawer-cart']) {
          refreshDrawer(data.sections['drawer-cart']);
        }
        // /cart/add.js returns the added line, not the full cart — fetch
        // /cart.js for the canonical item_count.
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            setBagCount(cart.item_count);
            document.dispatchEvent(new CustomEvent('cart:added', {
              detail: Object.assign({ cart: cart }, detail || {})
            }));
            openCartDrawer();
            return cart;
          });
      });
  };

  // Apply a discount code via Shopify's /discount/{code} endpoint
  // (sets the discount cookie on the cart), then refresh the drawer
  // and bag count from /cart.js with sections=drawer-cart.
  // Resolves with the cart object so callers can compare total_discount
  // before/after to decide whether the code actually applied.
  window.ZD.applyDiscount = function (code) {
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(function () {
        return fetch('/cart.js?sections=drawer-cart', { credentials: 'same-origin' });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.sections && cart.sections['drawer-cart']) {
          refreshDrawer(cart.sections['drawer-cart']);
        }
        setBagCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart: cart, source: 'discount' } }));
        return cart;
      });
  };

  // Mutate an existing cart line. quantity=0 removes the line.
  // /cart/change.js returns the full cart object directly, so no
  // separate /cart.js fetch is needed.
  window.ZD.cartChange = function (line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: 'drawer-cart' })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (cart) {
        if (cart.sections && cart.sections['drawer-cart']) {
          refreshDrawer(cart.sections['drawer-cart']);
        }
        setBagCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart: cart } }));
        return cart;
      });
  };

  // Delegated click handler on the cart drawer for Remove, qty +/-,
  // and the discount-code Apply button. The aside element itself is
  // never replaced (only its innerHTML), so this single listener
  // survives every drawer refresh.
  var drawerCartEl = document.getElementById('drawer-cart');
  if (drawerCartEl) {
    drawerCartEl.addEventListener('click', function (e) {
      // Discount code apply
      var discountBtn = e.target.closest('[data-cart-discount-submit]');
      if (discountBtn) {
        e.preventDefault();
        var form = discountBtn.closest('[data-cart-discount-form]');
        var input = form ? form.querySelector('input[name="discount_code"]') : null;
        var msgEl = drawerCartEl.querySelector('[data-cart-discount-msg]');
        var code = input ? input.value.trim() : '';
        if (!code) {
          if (msgEl) { msgEl.textContent = 'Enter a code.'; msgEl.className = 'drawer-cart-discount-msg is-error'; }
          return;
        }

        discountBtn.disabled = true;
        discountBtn.textContent = 'Applying…';
        if (msgEl) { msgEl.textContent = ''; msgEl.className = 'drawer-cart-discount-msg'; }

        window.ZD.applyDiscount(code)
          .then(function (cart) {
            // The drawer was just re-rendered — find the fresh msg el.
            var freshMsg = drawerCartEl.querySelector('[data-cart-discount-msg]');
            var codesUpper = (cart.cart_level_discount_applications || [])
              .map(function (app) { return String(app.title || '').toUpperCase(); }).join('|') + '|';
            var applied = codesUpper.indexOf(code.toUpperCase() + '|') !== -1;

            if (freshMsg) {
              if (applied) {
                freshMsg.textContent = 'Code applied.';
                freshMsg.className = 'drawer-cart-discount-msg is-success';
              } else {
                freshMsg.textContent = 'Invalid or expired code.';
                freshMsg.className = 'drawer-cart-discount-msg is-error';
              }
            }
          })
          .catch(function (err) {
            console.warn('[ZD discount apply]', err);
            var freshMsg = drawerCartEl.querySelector('[data-cart-discount-msg]');
            if (freshMsg) {
              freshMsg.textContent = 'Could not apply code. Try again.';
              freshMsg.className = 'drawer-cart-discount-msg is-error';
            }
            // discountBtn was re-rendered; nothing to restore
          });
        return;
      }

      var removeBtn = e.target.closest('[data-cart-remove]');
      var qtyBtn = e.target.closest('[data-cart-qty]');
      if (!removeBtn && !qtyBtn) return;

      var item = e.target.closest('[data-cart-line]');
      if (!item) return;
      var line = parseInt(item.getAttribute('data-cart-line'), 10);
      if (!line) return;

      e.preventDefault();

      var newQty;
      if (removeBtn) {
        newQty = 0;
      } else {
        var qtyEl = item.querySelector('[data-cart-qty-display]');
        var current = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;
        newQty = qtyBtn.getAttribute('data-cart-qty') === 'plus' ? current + 1 : current - 1;
        if (newQty < 0) newQty = 0;
      }

      // Optimistic visual: dim the row + lock buttons until response lands.
      item.style.opacity = '0.5';
      item.querySelectorAll('button').forEach(function (b) { b.disabled = true; });

      window.ZD.cartChange(line, newQty)
        .catch(function (err) {
          console.warn('[ZD cart change]', err);
          item.style.opacity = '';
          item.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
        });
    });

    // Enter-key in the discount input submits the form.
    drawerCartEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target.closest && e.target.closest('input[name="discount_code"]');
      if (!input) return;
      e.preventDefault();
      var btn = input.closest('[data-cart-discount-form]').querySelector('[data-cart-discount-submit]');
      if (btn) btn.click();
    });
  }

  // Intercept PDP product form submit. Sticky-ATC button calls
  // form.requestSubmit() so it routes through this handler too.
  var pdpForm = document.getElementById('pdp-form');
  if (pdpForm) {
    pdpForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var atcBtn = pdpForm.querySelector('[data-pdp-atc-btn]');
      var stickyBtn = document.querySelector('[data-pdp-sticky-atc-btn]');
      var origText = atcBtn ? atcBtn.innerHTML : '';
      var origSticky = stickyBtn ? stickyBtn.textContent : '';

      if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Adding…'; }
      if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = 'Adding…'; }

      window.ZD.cartAdd(new FormData(pdpForm), { source: 'pdp' })
        .then(function () {
          if (atcBtn) { atcBtn.disabled = false; atcBtn.innerHTML = origText; }
          if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = origSticky; }
        })
        .catch(function (err) {
          var msg = (err && err.description) || (err && err.message) || 'Could not add to bag.';
          console.warn('[ZD cart add]', msg);
          if (atcBtn) atcBtn.textContent = 'Try again';
          if (stickyBtn) stickyBtn.textContent = 'Try again';
          setTimeout(function () {
            if (atcBtn) { atcBtn.disabled = false; atcBtn.innerHTML = origText; }
            if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = origSticky; }
          }, 1500);
        });
    });
  }
})();
